import {
  ANGLE_MARK_ARM_FRACTION,
  ANGLE_MARK_NOMINAL_RADIUS,
  ANGLE_MARK_TICK_GAP,
  ANGLE_MARK_VALUE_CLEARANCE,
  ANGLE_MARK_VALUE_INSET,
  angleMarkValueDistance,
} from '@/domain/construction';
import { type Vec2 } from '@/domain/math';

/**
 * The geometry of an angle mark, in screen space.
 *
 * Shared by the canvas painter and both exporters, so a mark is the same shape and the same
 * size wherever it is drawn. Working in screen units is the whole point: a mark is notation,
 * not geometry — like a point's dot, it has to stay legible at every zoom rather than growing
 * with the figure.
 *
 * ## Sizing, and why it is clamped
 *
 * A fixed screen size is right until the angle's arms are shorter than the mark. Then the
 * square sticks out past the ends of the very lines it is marking, which reads as a mistake
 * even to someone who could not say why. The radius is therefore the smaller of the nominal
 * size and a third of the shorter arm, so the mark is always visibly *inside* the angle.
 *
 * Below a floor it is not drawn at all. A two-pixel square is a smudge, and a smudge at a
 * vertex is worse than nothing — the reader sees ink and cannot tell what it means.
 */

/**
 * Nominal radius of the mark, in screen pixels.
 *
 * Taken from the domain rather than declared here, because `anchorPosition` has to apply the
 * same clamp to place the caption and a second copy is how the two came apart: the domain knew
 * only about the arm fraction, so on a long-armed figure it anchored an `α` 360 units out from
 * an arc of radius 22.
 */
export const NOMINAL_RADIUS = ANGLE_MARK_NOMINAL_RADIUS;

/** The mark never occupies more than this fraction of the shorter arm. */
export const ARM_FRACTION = ANGLE_MARK_ARM_FRACTION;

/** Below this the mark is illegible and is omitted. */
export const MINIMUM_RADIUS = 7;

/** Gap between successive arcs of a multi-tick mark, in screen pixels. Shared, as above. */
export const TICK_GAP = ANGLE_MARK_TICK_GAP;

/** Where the measure sits, as a fraction of the radius. Inside the arc. Shared, as above. */
export const VALUE_INSET = ANGLE_MARK_VALUE_INSET;

/** Room a measure pushed outside gets beyond the gap a name gets. Shared, as above. */
export const VALUE_CLEARANCE = ANGLE_MARK_VALUE_CLEARANCE;

/** The parts of a mark that are not its three points. Grouped so the call stays readable. */
export interface AngleMarkOptions {
  /** Size when the arms leave room for it, in the output's own units. */
  readonly nominalRadius?: number;
  /** The user's size multiplier, applied after the arm clamp. See below for why after. */
  readonly size?: number;
  /** Where the user dragged the measure, in **world** units. */
  readonly valueOffset?: Vec2;
}

const ZERO: Vec2 = Object.freeze({ x: 0, y: 0 });

export interface AngleMarkGeometry {
  /** Vertex in screen coordinates. */
  readonly vertex: Vec2;
  /** Radius actually used, after clamping. */
  readonly radius: number;
  /** Screen-space angle of each arm, in radians. */
  readonly startAngle: number;
  readonly endAngle: number;
  /** True when the sweep runs anticlockwise in screen space. */
  readonly counterclockwise: boolean;
  /**
   * How wide the angle opens *on screen*, in degrees, always positive.
   *
   * Screen rather than space, because what has to fit inside the wedge is a piece of text drawn
   * on the screen. A 90° angle seen edge-on in a projection is a narrow wedge, and a number
   * written into it would overflow however square the angle really is.
   */
  readonly sweepDegrees: number;
  /** Direction of the angle's bisector, for placing the value. */
  readonly bisector: Vec2;
  /**
   * The user's drag of the measure, converted to this output's units and handedness.
   *
   * Converted here rather than by the callers because this is the one function that holds
   * `toScreen`, and a world displacement cannot be turned into a screen one without it — under
   * a y-flip the sign changes, and under zoom the length does.
   */
  readonly valueShift: Vec2;
}

/**
 * Works out where and how big the mark is.
 *
 * Returns `null` when there is not enough room to draw something legible, which the callers
 * treat as "draw nothing" — an honest outcome rather than a smudge.
 *
 * `toScreen` is passed in rather than the transform, so this works equally for a canvas, an
 * SVG viewport and a PDF page, which do not agree about which way y runs.
 */
export function angleMarkGeometry(
  vertexWorld: Vec2,
  fromWorld: Vec2,
  toWorld: Vec2,
  armLengthWorld: number,
  toScreen: (world: Vec2) => Vec2,
  options: AngleMarkOptions = {}
): AngleMarkGeometry | null {
  const { nominalRadius = NOMINAL_RADIUS, size = 1, valueOffset = ZERO } = options;
  const vertex = toScreen(vertexWorld);

  /*
   * The arms are converted by projecting a point along each and subtracting, rather than by
   * transforming the direction vectors. A direction is not a position: under any transform
   * with a translation — which every one of these has — transforming it directly gives an
   * arm pointing at the origin of the output space.
   */
  const armFor = (direction: Vec2): Vec2 => {
    const tip = toScreen({
      x: vertexWorld.x + direction.x * armLengthWorld,
      y: vertexWorld.y + direction.y * armLengthWorld,
    });
    return { x: tip.x - vertex.x, y: tip.y - vertex.y };
  };

  const shiftFor = (offset: Vec2): Vec2 => {
    if (offset.x === 0 && offset.y === 0) return ZERO;
    const moved = toScreen({ x: vertexWorld.x + offset.x, y: vertexWorld.y + offset.y });
    return { x: moved.x - vertex.x, y: moved.y - vertex.y };
  };

  const first = armFor(fromWorld);
  const second = armFor(toWorld);

  const firstLength = Math.hypot(first.x, first.y);
  const secondLength = Math.hypot(second.x, second.y);
  if (firstLength < 1e-9 || secondLength < 1e-9) return null;

  /*
   * `size` multiplies *after* the arm clamp, not before it.
   *
   * The order is the whole behaviour of the panel's size buttons, and it was the other way
   * round here while the domain had it this way — the fourth drift of this arithmetic. Clamping
   * afterwards means the clamp swallows the request: on any figure whose arms are short enough
   * for the clamp to bite, "to" and "rất to" drew exactly the same arc as "vừa", so the buttons
   * appeared dead. Worse, `anchorPosition` used the domain's order, so a name was placed against
   * a radius the arc was never drawn at.
   *
   * The clamp is the right *default* — an arc must not stick out past the arms it marks. But a
   * size the user chose is not a default; it is the user overruling one, which is the only
   * reason the control exists. The clamp decides the ordinary size, and `size` scales that.
   */
  const armOnScreen = Math.min(firstLength, secondLength);
  const radius = Math.min(nominalRadius, armOnScreen * ARM_FRACTION) * size;
  if (radius < MINIMUM_RADIUS) return null;

  const startAngle = Math.atan2(first.y, first.x);
  const endAngle = Math.atan2(second.y, second.x);

  /*
   * The mark belongs *inside* the angle, which means sweeping the short way round. Taking
   * the long way would draw an arc across the reflex side — the mark would be sitting in the
   * wrong region entirely, and on an acute angle it would look like a mark on a different
   * angle altogether.
   */
  let sweep = endAngle - startAngle;
  while (sweep <= -Math.PI) sweep += Math.PI * 2;
  while (sweep > Math.PI) sweep -= Math.PI * 2;

  const half = startAngle + sweep / 2;

  return {
    vertex,
    radius,
    startAngle,
    endAngle,
    counterclockwise: sweep < 0,
    sweepDegrees: Math.abs(sweep) * (180 / Math.PI),
    bisector: { x: Math.cos(half), y: Math.sin(half) },
    // Same trick as the arms above, and for the same reason: a displacement is not a position,
    // so it is converted by moving a point and subtracting rather than by transforming it.
    valueShift: shiftFor(valueOffset),
  };
}

/**
 * The corners of the right-angle square, in screen coordinates.
 *
 * Only the two *inner* sides are drawn — the L. The outer two would lie along the arms
 * themselves, and stroking over an existing line makes it look thicker at the vertex rather
 * than marked, which is the opposite of the intent. Every textbook draws the L.
 */
export function rightAngleCorners(geometry: AngleMarkGeometry): readonly [Vec2, Vec2, Vec2] {
  const { vertex, radius, startAngle, endAngle } = geometry;

  const along = (angle: number): Vec2 => ({
    x: vertex.x + Math.cos(angle) * radius,
    y: vertex.y + Math.sin(angle) * radius,
  });

  const first = along(startAngle);
  const second = along(endAngle);
  // The far corner of the square: the two offsets added, which for a right angle is exactly
  // the corner opposite the vertex.
  const far = {
    x: first.x + second.x - vertex.x,
    y: first.y + second.y - vertex.y,
  };

  return [first, far, second];
}

/**
 * Where the measure is written: **inside** the arc when it fits there, on the bisector.
 *
 * It used to be written outside unconditionally, past the arc and past every tick. That put the
 * one piece of text belonging to this angle out in the band of space where the names of the
 * surrounding points live, so on a busy corner the reader had to work out which mark a number
 * went with. Inside the arc there is no such question — the arc encloses it.
 *
 * ## Why "when it fits" and not always
 *
 * Because a wedge is a triangle. Rendering a right angle, a 40° angle and a named mark side by
 * side showed the first and third right and the second wrong: `56.3°` written into a 40° opening
 * came out across its own arc and over the vertex. The room inside grows with the angle, and a
 * narrow angle has none, so a narrow angle gets its number outside — which is what a drafting
 * text does with one too.
 *
 * Outside, the ticks matter: they step outward from the radius, so clearing the mark means
 * clearing the outermost of them. Inside they do not, since inside the innermost arc is inside
 * all of them.
 */
export function valuePosition(geometry: AngleMarkGeometry, ticks = 1): Vec2 {
  const distance = angleMarkValueDistance(geometry.radius, geometry.sweepDegrees, ticks);
  return {
    x: geometry.vertex.x + geometry.bisector.x * distance + geometry.valueShift.x,
    y: geometry.vertex.y + geometry.bisector.y * distance + geometry.valueShift.y,
  };
}

/** Radius of each arc in a multi-tick mark, innermost first. */
export function tickRadii(geometry: AngleMarkGeometry, ticks: number): readonly number[] {
  return Array.from({ length: ticks }, (_, index) => geometry.radius + index * TICK_GAP);
}
